import { useContext, useMemo } from 'react';
import { faExternalLinkAlt, faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactReduxContext } from 'react-redux';
import styled from 'styled-components';
import strings from '../../../translations';
import { setWarning } from '../../../state/slices/uiSlice';

const StyledMasterGroupHeader = styled.div`
	  z-index: 1;
  height: auto;
  display: flex;
   justify-content: space-between;
   align-items: center;
   cursor: pointer;
   background-color: ${(props) =>
     props.bgColor ? props.bgColor : props.theme.colors.secondaryColorGreen};
   border-radius: ${(props) => (props.isOpen ? '4px 4px 0px 0px' : '4px')};
   box-shadow: 0px 3px 6px 0px rgb(0 0 0 / 16%);
   @-moz-document url-prefix() {
    position: initial;
  }

  /* ensure name can expand */
  overflow: visible;
`;

const StyledLayerGroups = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 8px 0px 8px 0px;
  padding-left: ${(props) => (props.isFirstSubgroup ? '0px' : '16px')};
  &:last-child {
    ${(props) =>
      props.parentId === -1
        ? '1px solid ' + props.theme.colors.mainColor2
        : 'none'};
  }

  /* ensure header expansion isn't clipped by this container */
  overflow: visible;
`;

const StyledLeftContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledMasterGroupHeaderIcon = styled.div`
  width: 48px;
  margin: 1em 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    font-size: 20px;
    color: ${(props) => props.theme.colors.mainWhite};
  }
`;

const StyledMasterGroupLinkIcon = styled.div`
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    font-size: 18px;
    color: ${(props) => props.theme.colors.mainWhite};
  }
`;

const StyledRightContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledMasterGroupName = styled.p`
  user-select: none;

  /* When expanded we remove truncation so full name displays on multiple lines */
  white-space: ${(props) => (props.$expanded ? 'normal' : 'nowrap')};
  overflow: ${(props) => (props.$expanded ? 'visible' : 'hidden')};
  text-overflow: ellipsis;
  max-width: ${(props) => (props.$expanded ? 'none' : '230px')};
  color: ${(props) => props.theme.colors.mainWhite};
  margin: 0;
  padding: 8px;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.1s ease-in;
   cursor: pointer;
 `;

export const LinkGroup = ({
  group,
  link,
  index,
  isFirstSubgroup,
  ...rest
}) => {
  const { store } = useContext(ReactReduxContext);
  const lang = useMemo(() => strings.getLanguage(), []);
  const defaultLang = useMemo(() => strings.getAvailableLanguages()[0], []);
  const groupName =
    group.locale[lang].name || group.locale[defaultLang].name || null;

  const handleLinkClick = (event, link) => {
    event.preventDefault();
    const savedState = localStorage.getItem('dontShowExitLinkWarn');
    if (!savedState) {
      store.dispatch(
        setWarning({
          title: strings.exitConfirmation,
          subtitle: strings.layerlist.linkAddress + link,
          confirm: {
            text: strings.general.continue,
            action: () => {
              window.open(link, '_blank');
              store.dispatch(setWarning(null));
            }
          },
          cancel: {
            text: strings.general.cancel,
            action: () => {
              store.dispatch(setWarning(null));
            }
          },
          dontShowAgain: {
            id: 'dontShowExitLinkWarn'
          }
        })
      );
    } else {
      window.open(link, '_blank');
    }
  };

  const idName = group.locale?.[lang]?.name || index;

  return (
    <>
      <StyledLayerGroups
        key={`link-container-${idName}`}
        id={`link-layer-group-${idName}`}
        isFirstSubgroup={isFirstSubgroup}
        index={index}
        {...rest}
      >
        <StyledMasterGroupHeader
          key={`link-group-${idName}`}
          id={`link-group-header-${idName}`}
          onClick={(e) => handleLinkClick(e, link)}
        >
          <StyledLeftContent>
            <StyledMasterGroupHeaderIcon>
              <FontAwesomeIcon icon={faLink} />
            </StyledMasterGroupHeaderIcon>
          </StyledLeftContent>

          <StyledMasterGroupName
            title={groupName}
            aria-label={`Open ${groupName} link`}
            tabIndex="0"
          >
            {groupName}
          </StyledMasterGroupName>

          <StyledRightContent>
            <StyledMasterGroupLinkIcon>
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </StyledMasterGroupLinkIcon>
          </StyledRightContent>
        </StyledMasterGroupHeader>
      </StyledLayerGroups>
    </>
  );
};
